"use client";
import React, { useState, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useMouseMovement } from "@/app/[lang]/MouseMovementContext";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getBaseUrl } from "@/util/get-base-url";
import IFrameLoader from "./iframe-loader";
import { hexToRgb } from "@mui/material";
import "./gallery.css"


export type ImageType = {
    id: string;
    imageUrl: string;
    imageAlt: string;
};

export type TravelContent = {
    id: string;
    imageDesktop: string;
    imageMobile: string;
    imageAlt: string;
    title: string;
    titleColor: string;
    des: string;
    desColor: string;
    src: string;
    image: ImageType[];
}

interface DialogImageProps {
    colorStyle: any;
    trip: TravelContent;
    priorityImage: boolean;
    priorityContent: boolean;
}

const dialogImage = (props: DialogImageProps) => {

    const { colorStyle, trip, priorityImage, priorityContent } = props;

    const { hasMouseMoved } = useMouseMovement();
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    const [emblaRefMobile, emblaApiMobile] = useEmblaCarousel({ loop: false, dragFree: true });
    const [emblaRefDesktop, emblaApiDesktop] = useEmblaCarousel({ loop: false, dragFree: true });

    const scrollNext = () => {
        if (window.innerWidth < 768) {
          emblaApiMobile?.scrollNext();
        } else {
          emblaApiDesktop?.scrollNext();
        }
    };
      
    const scrollPrev = () => {
        if (window.innerWidth < 768) {
            emblaApiMobile?.scrollPrev();
        } else {
            emblaApiDesktop?.scrollPrev();
        }
    };

    const openModal = (imgUrl: string) => {
        setSelectedImage(imgUrl);
    };

    const closeModal = () => {
        setSelectedImage(null);
    };

    const hexToRgbWithOpacity = (hex: string, opacity: number = 0.5): string => {
        const rgb = hexToRgb(hex)?.match(/\d+/g);
        if (rgb) {
            return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;
        }
        return hex;
    };

    function chunkArray(arr: ImageType[], size: number) {
        if (arr.length > 0 && size > 0) {
            const arrayFormat = Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
                arr.slice(i * size, i * size + size)
              )
            return arrayFormat
        }
    }

    return (trip.title || trip.des || trip.src || (trip.image && trip.image.length > 0)) && (
        <div className="flex flex-col gap-8 sm:gap-16 relative w-full items-center py-[40px] px-[30px] sm:px-[120px]">
            {(trip.src == "" && trip.image.length == 0 && (trip.title || trip.des)) && 
                <div className="flex flex-col gap-4 items-center text-center">
                    {trip.title && <h2
                        className="font-medium text-3xl text-wrap whitespace-pre-line"
                        style={{ color: colorStyle?.headingMain || "#254b7c" }}>
                        {trip.title}
                    </h2>}
                    {trip.des && <p
                        className="font-light text-base text-wrap whitespace-pre-line"
                        style={{ color: colorStyle?.headingSub || "#2c568a" }}>
                        {trip.des}
                    </p>}
                </div>
            }
            {(trip.src && (trip.title || trip.des)) && (
                <div className="flex flex-col gap-4 items-center text-center">
                    {trip.title && <h2
                        className="font-medium text-3xl text-wrap whitespace-pre-line"
                        style={{ color: colorStyle?.headingMain || "#254b7c" }}>
                        {trip.title}
                    </h2>}
                    {trip.des && <p
                        className="font-light text-base text-wrap whitespace-pre-line"
                        style={{ color: colorStyle?.headingSub || "#2c568a" }}>
                        {trip.des}
                    </p>}
                </div>
            )}

            { (trip.src || (trip.image && trip.image.length > 0)) && (
            <div className="flex flex-col sm:flex-row gap-10 w-full">
                {trip.src ? (
                    <div className="flex md:flex-row flex-col gap-10 w-full ">
                        <div
                            className={`relative ${(trip.image && trip.image.length > 0) ? "md:w-1/2 w-full sm:flex-1" : "w-full flex items-center justify-center"}  h-full`}>
                                {hasMouseMoved ? (
                                    <div className={`${(trip.image && trip.image.length > 0) ? "" : "aspect-video w-full max-w-[1440px]"}`}>
                                        <IFrameLoader src={trip.src} showImage={trip.image && trip.image.length > 0}/>
                                    </div>
                                ) : (
                                    <div 
                                        className="aspect-video bg-gray-300" 
                                        style={{
                                            animation: 'fade 1.5s ease-in-out infinite'
                                        }}
                                    />
                                )}
                        </div>
                        {(trip.image && trip.image.length > 0) && (
                            <div className="overflow-hidden md:w-1/2 w-full">
                                <div className="overflow-hidden" ref={emblaRefDesktop}>
                                    <div className="flex gap-4">
                                    {
                                        (chunkArray(trip.image, 6) || []).map((chunk, idx) => (
                                        <div key={idx} className="flex-none w-full h-auto grid grid-cols-3 grid-rows-2 gap-4 shrink-0 justify-items-center">
                                            {chunk.map((item, i) => (
                                                <div key={i} className="w-full aspect-[1/1] relative overflow-hidden">
                                                    {hasMouseMoved ? (
                                                    <picture key={item.id}>
                                                    <source srcSet={`${getBaseUrl(item.imageUrl)}_resize.avif`} type="image/avif" />
                                                    <source srcSet={`${getBaseUrl(item.imageUrl)}_resize.webp`} type="image/webp" />
                                                        <img
                                                            src={item.imageUrl}
                                                            alt={item.imageAlt}
                                                            className="w-full h-full cursor-pointer object-cover"
                                                            width={250}
                                                            height={250}
                                                            style={{ width: "100%", height: "100%", aspectRatio: 1/1 }}
                                                            loading={priorityImage ? undefined : "lazy"}
                                                            onClick={() => openModal(item.imageUrl)}
                                                        />
                                                    </picture>
                                                    ): <div 
                                                            className="w-[250px] h-[250px] bg-gray-300" 
                                                            style={{
                                                                animation: 'fade 1.5s ease-in-out infinite'
                                                            }}
                                                        /> 
                                                    } 
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                    </div>
                                    {/* Modal for showing large image */}
                                    {selectedImage && (
                                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 pt-28"
                                        onClick={closeModal}
                                        >
                                            <div className="relative overflow-auto"
                                            onClick={(e) => e.stopPropagation()}
                                            >
                                                <img
                                                    src={`${selectedImage}`}
                                                    alt="Large view"
                                                    width={512}
                                                    height={512}
                                                    className="h-auto max-h-[70vh] xl:w-[30vw] md:w-[50vw] w-[60vw] object-contain"
                                                    loading="lazy"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {trip.image.length > 6 && (
                                        <div className="w-full flex justify-between items-center mt-8">
                                            <div className="flex w-full justify-end">
                                                <button
                                                    aria-label="Previous"
                                                    onClick={scrollPrev}
                                                    className="embla__button embla__button--prev inline-flex items-center justify-center 
                                                        whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none 
                                                        focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none 
                                                        disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden 
                                                        relative transform left-0 top-0 translate-y-0 mx-1 dynamic-bg dynamic-color dynamic-border"
                                                    style={
                                                        {
                                                            "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                                            "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                                            "--dynamic-border": colorStyle?.buttonTextNormal || "#ffffff",
                                                            "--dynamic-hover-border": colorStyle?.buttonTextHover || "#ffffff",
                                                            "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                                            "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                                            "--dynamic-focus-color": colorStyle?.buttonTextNormal || "#ffffff",
                                                            "--dynamic-hover-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                                        } as React.CSSProperties
                                                    }
                                                >
                                                    <ChevronLeft className="h-5 w-5" />
                                                </button>
                                                <button
                                                    aria-label="Next"
                                                    onClick={scrollNext}
                                                    className="embla__button embla__button--next inline-flex items-center justify-center 
                                                        whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none 
                                                        focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none 
                                                        disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full -right-12 max-md:hidden 
                                                        relative transform left-0 top-0 translate-y-0 mx-1 dynamic-bg dynamic-color dynamic-border"
                                                    style={
                                                        {
                                                            "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                                            "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                                            "--dynamic-border": colorStyle?.buttonTextNormal || "#ffffff",
                                                            "--dynamic-hover-border": colorStyle?.buttonTextHover || "#ffffff",
                                                            "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                                            "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                                            "--dynamic-focus-color": colorStyle?.buttonTextNormal || "#ffffff",
                                                            "--dynamic-hover-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                                        } as React.CSSProperties
                                                    }
                                                >
                                                    <ChevronRight className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ) : ((trip.image && trip.image.length > 0) && (
                    <>
                    <div className="flex flex-col gap-8 sm:gap-16 relative w-full items-center">
                        {((trip.title || trip.des) && trip.image.length > 12) && (<div className="flex flex-row justify-center w-full">
                            {trip.image.length > 12 && (<div className="w-1/5"></div>)}
                            {(trip.title || trip.des) && (
                            <div className={`flex flex-col w-full md:w-3/5 gap-4 items-center text-center`}>
                                {trip.title && <h2
                                    className="font-medium text-3xl text-wrap whitespace-pre-line"
                                    style={{ color: colorStyle?.headingMain || "#254b7c" }}>
                                    {trip.title}
                                </h2>}
                                {trip.des && <p
                                    className="font-light text-base text-wrap whitespace-pre-line"
                                    style={{ color: colorStyle?.headingSub || "#2c568a" }}>
                                    {trip.des}
                                </p>}
                            </div>)}
                            {trip.image.length > 12 && (
                                <div className="w-1/5 flex justify-between items-start mt-2 mr-2">
                                    <div className="flex w-full justify-end">
                                        <button
                                            aria-label="Previous"
                                            onClick={scrollPrev}
                                            className="embla__button embla__button--prev inline-flex items-center justify-center 
                                                whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none 
                                                focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none 
                                                disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full max-md:hidden 
                                                relative transform left-0 top-0 translate-y-0 mx-1 dynamic-bg dynamic-color dynamic-border"
                                            style={
                                                {
                                                    "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                                    "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                                    "--dynamic-border": colorStyle?.buttonTextNormal || "#ffffff",
                                                    "--dynamic-hover-border": colorStyle?.buttonTextHover || "#ffffff",
                                                    "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                                    "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                                    "--dynamic-focus-color": colorStyle?.buttonTextNormal || "#ffffff",
                                                    "--dynamic-hover-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                                } as React.CSSProperties
                                            }
                                        >
                                            <ChevronLeft className="h-5 w-5" />
                                        </button>
                                        <button
                                            aria-label="Next"
                                            onClick={scrollNext}
                                            className="embla__button embla__button--next inline-flex items-center justify-center 
                                                whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none 
                                                focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none 
                                                disabled:opacity-50 text-base font-normal border bg-transparent h-10 w-10 rounded-full -right-12 max-md:hidden 
                                                relative transform left-0 top-0 translate-y-0 mx-1 dynamic-bg dynamic-color dynamic-border"
                                            style={
                                                {
                                                    "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                                    "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                                    "--dynamic-border": colorStyle?.buttonTextNormal || "#ffffff",
                                                    "--dynamic-hover-border": colorStyle?.buttonTextHover || "#ffffff",
                                                    "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                                    "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                                    "--dynamic-focus-color": colorStyle?.buttonTextNormal || "#ffffff",
                                                    "--dynamic-hover-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                                } as React.CSSProperties
                                            }
                                        >
                                            <ChevronRight className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>)}
                        <div className="hidden md:flex overflow-hidden">
                            <div className="overflow-hidden" ref={emblaRefDesktop}>
                                <div className={`flex xl:gap-12 md:gap-8 gap-6`}>
                                {(
                                    (chunkArray(trip.image, 12) || []).map((chunk, idx) => (
                                    <div key={idx} className={`flex-none w-full h-auto grid grid-cols-4 ${trip.image.length <= 12 ? "" : "grid-rows-3"} xl:gap-12 md:gap-8 gap-6 justify-items-center`}>
                                        {chunk.map((item, i) => (
                                            <div key={i} className="w-full aspect-[1/1] relative overflow-hidden">
                                                {hasMouseMoved ? (
                                                    <picture key={item.id}>
                                                    <source srcSet={`${getBaseUrl(item.imageUrl)}_resize.avif`} type="image/avif" />
                                                    <source srcSet={`${getBaseUrl(item.imageUrl)}_resize.webp`} type="image/webp" />
                                                        <img
                                                            src={item.imageUrl}
                                                            alt={item.imageAlt}
                                                            className="w-full h-full cursor-pointer object-cover"
                                                            width={250}
                                                            height={250}
                                                            style={{ width: "100%", height: "100%", aspectRatio: 1/1 }}
                                                            loading={priorityImage ? undefined : "lazy"}
                                                            onClick={() => openModal(item.imageUrl)}
                                                        />
                                                    </picture>
                                                ) :
                                                    <div 
                                                        className="w-[250px] h-[250px] bg-gray-300" 
                                                        style={{
                                                            animation: 'fade 1.5s ease-in-out infinite'
                                                        }}
                                                    />
                                                }
                                            </div>
                                        ))}
                                    </div>
                                )))}
                                </div>
                                {/* Modal for showing large image */}
                                {selectedImage && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 pt-28"
                                    onClick={closeModal}
                                    >
                                        <div className="relative overflow-auto"
                                        onClick={(e) => e.stopPropagation()}
                                        >
                                            <img
                                                src={`${selectedImage}`}
                                                alt="Large view"
                                                width={512}
                                                height={512}
                                                className="h-auto max-h-[70vh] xl:w-[30vw] md:w-[50vw] w-[60vw] object-contain"
                                                loading="lazy"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                )}
                                
                            </div>
                        </div>
                        <div className="flex md:hidden overflow-hidden">
                            <div className="overflow-hidden" ref={emblaRefMobile}>
                                <div className={`flex gap-4`}>
                                {(trip.image && trip.image.length > 0) &&(
                                    (chunkArray(trip.image, 4) || []).map((chunk, idx) => (
                                    <div key={idx} className={`flex-none w-full h-auto grid grid-cols-2 grid-rows-2 gap-4 justify-items-center`}>
                                        {chunk.map((item, i) => (
                                            <div key={i} className="w-full aspect-[1/1] relative overflow-hidden">
                                                {hasMouseMoved ? (
                                                    <picture key={item.id}>
                                                    <source srcSet={`${getBaseUrl(item.imageUrl)}_resize.avif`} type="image/avif" />
                                                    <source srcSet={`${getBaseUrl(item.imageUrl)}_resize.webp`} type="image/webp" />
                                                        <img
                                                            src={item.imageUrl}
                                                            alt={item.imageAlt}
                                                            className="w-full h-full cursor-pointer object-cover"
                                                            width={250}
                                                            height={250}
                                                            style={{ width: "100%", height: "100%", aspectRatio: 1/1 }}
                                                            loading={priorityImage ? undefined : "lazy"}
                                                            onClick={() => openModal(item.imageUrl)}
                                                        />
                                                    </picture>
                                                ) :
                                                    <div 
                                                        className="w-[250px] h-[250px] bg-gray-300" 
                                                        style={{
                                                            animation: 'fade 1.5s ease-in-out infinite'
                                                        }}
                                                    />
                                                }
                                            </div>
                                        ))}
                                    </div>
                                )))}
                                </div>
                                {/* Modal for showing large image */}
                                {selectedImage && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 pt-28"
                                    onClick={closeModal}
                                    >
                                        <div className="relative overflow-auto"
                                        onClick={(e) => e.stopPropagation()}
                                        >
                                            <img
                                                src={`${selectedImage}`}
                                                alt="Large view"
                                                width={512}
                                                height={512}
                                                className="h-auto max-h-[70vh] xl:w-[30vw] md:w-[50vw] w-[60vw] object-contain"
                                                loading="lazy"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                )}
                                
                            </div>
                        </div>
                    </div>
                    
                    </>
                ))
            }
            </div>
            )}
        </div>
    );
};

export default dialogImage;
